import { LineChart, Line, YAxis, XAxis, Tooltip } from "recharts";

interface SparklineProps {
  data: Record<string, unknown>[];
  lines: { key: string; color: string }[];
  showLegend?: boolean;
  width?: number;
  height?: number;
}

export function Sparkline({
  data,
  lines,
  width = 200,
  height = 80,
}: SparklineProps) {
  // Calculate min and max for better scaling across all active lines
  let minValue = Infinity;
  let maxValue = -Infinity;

  data.forEach((item) => {
    lines.forEach((line) => {
      const val = item[line.key];
      if (typeof val === "number" && !isNaN(val)) {
        if (val < minValue) minValue = val;
        if (val > maxValue) maxValue = val;
      }
    });
  });

  // Fallbacks if no valid numeric data found
  if (minValue === Infinity) minValue = 0;
  if (maxValue === -Infinity) maxValue = 100;

  // Add 20% padding to the top and bottom for visual breathing room
  const range = maxValue - minValue;
  const padding = range * 0.2 || 5;

  // Custom Tooltip component to show Date, Responder, and scores for each line

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: readonly Record<string, unknown>[];
  }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload as Record<string, unknown>;
      return (
        <div className="bg-white p-3 border border-slate-200 shadow-xl rounded-lg text-[11px] pointer-events-none z-50">
          <div className="font-bold text-slate-900 mb-1 border-b border-slate-100 pb-1">
            {String(dataPoint.date)}
          </div>
          <div className="text-slate-500 mb-2 italic">
            By: {String(dataPoint.responder)}
          </div>
          <div className="flex flex-col gap-1.5">
            {payload.map((entry: Record<string, unknown>, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: entry.color as string }}
                  />
                  <span className="text-slate-700 font-medium">
                    {String(entry.name)}:
                  </span>
                </div>
                <span className="font-bold text-slate-900">
                  {String(entry.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <LineChart
      width={width}
      height={height}
      data={data}
      margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
    >
      <defs>
        {lines.map((line) => (
          <linearGradient
            key={`grad-${line.key}`}
            id={`sparkline-gradient-${line.color.replace("#", "")}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor={line.color} stopOpacity={0.1} />
            <stop offset="100%" stopColor={line.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>

      {/* XAxis is required for the Tooltip to trigger correctly on hover */}
      <XAxis dataKey="id" hide />
      <YAxis domain={[minValue - padding, maxValue + padding]} hide />

      <Tooltip
        content={({ active, payload }) => (
          <CustomTooltip
            active={active}
            payload={
              payload as unknown as
                | readonly Record<string, unknown>[]
                | undefined
            }
          />
        )}
        cursor={{ stroke: "#94A3B8", strokeWidth: 1, strokeDasharray: "4 4" }}
        isAnimationActive={false}
        wrapperStyle={{ zIndex: 1000 }}
        position={{ y: -200 }}
      />

      {lines.map((line) => (
        <Line
          key={line.key}
          name={line.key}
          type="monotone"
          dataKey={line.key}
          stroke={line.color}
          strokeWidth={2}
          dot={{ r: 3, fill: line.color, strokeWidth: 0 }}
          activeDot={{ r: 5, strokeWidth: 0 }}
          fill={`url(#sparkline-gradient-${line.color.replace("#", "")})`}
          fillOpacity={1}
          isAnimationActive={false}
          connectNulls={true}
        />
      ))}
    </LineChart>
  );
}
